import { Bell, BellOff, Loader2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { useToast } from "@/hooks/use-toast";
import { getErrorMessage } from "@/lib/apiErrors";

export function PushNotificationToggle() {
  const {
    isSupported,
    permissionState,
    isSubscribed,
    needsResubscription,
    isSubscribing,
    isUnsubscribing,
    requestPermission,
    subscribe,
    unsubscribe,
  } = usePushNotifications();
  const { toast } = useToast();

  if (!isSupported) {
    return (
      <div className="flex items-center gap-3 text-muted-foreground">
        <BellOff className="h-4 w-4" />
        <span className="text-sm">Push notifications are not supported in this browser.</span>
      </div>
    );
  }

  const handleToggle = async () => {
    try {
      if (isSubscribed) {
        await unsubscribe();
        toast({
          title: "Notifications disabled",
          description:
            "You will no longer receive push notifications on this device.",
        });
      } else {
        if (permissionState === "denied") {
          toast({
            title: "Permission denied",
            description:
              "Please enable notifications in your browser settings.",
            variant: "destructive",
          });
          return;
        }

        if (permissionState === "default") {
          const permission = await requestPermission();
          if (permission !== "granted") {
            toast({
              title: "Permission required",
              description:
                "Notification permission is required to enable push notifications.",
              variant: "destructive",
            });
            return;
          }
        }

        const subscription = await subscribe(navigator.platform || "Unknown Device");
        // subscribe() resolves to null, not an error, when permission wasn't granted or the
        // server has no VAPID key configured - nothing was registered in either case
        if (!subscription) {
          toast({
            title: "Notifications not enabled",
            description:
              "Push notifications couldn't be set up on this device.",
            variant: "destructive",
          });
          return;
        }
        toast({
          title: "Notifications enabled",
          description:
            "You will now receive push notifications on this device.",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: getErrorMessage(error, "Failed to update notification settings"),
        variant: "destructive",
      });
    }
  };

  const isLoading = isSubscribing || isUnsubscribing;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Bell className="h-4 w-4 text-muted-foreground" />
          <div>
            <Label htmlFor="push-notifications" className="cursor-pointer font-medium">
              Push Notifications for This Device
            </Label>
            <p className="text-sm text-muted-foreground">
              Receive notifications on this browser even when closed. Enable separately on each device.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
          <Switch
            id="push-notifications"
            checked={isSubscribed}
            onCheckedChange={handleToggle}
            disabled={isLoading || permissionState === "denied"}
          />
        </div>
      </div>
      {permissionState === "denied" && (
        <p className="text-sm text-destructive">
          Notifications are blocked. Please update your browser settings to enable.
        </p>
      )}
      {needsResubscription && isSubscribing && (
        <p className="text-sm text-muted-foreground">
          Restoring push notifications...
        </p>
      )}
    </div>
  );
}
